import { useState, useCallback, useMemo } from "react";

import { removeItem } from "../../../services/removeItem";
import { updateQuantity } from "../../../services/updateQuantity";
import { CartAction, CartGroupView, CartView, CTLineItem } from "../../../types/cartTypes";
import { normalizeToGroupedMainItem, normalizeToAddOns } from "../../../utils/cartNormalization";
import { isPassProduct } from "../../../utils/cartPageHelpers";
import { updateCart, getCart } from "../../../utils/cartUtil";
import { MiniBasketDisplayItem } from "../miniBasketTypes";

/**
 * Custom hook to handle business logic and item manipulation for MiniBasket.
 */
export const useMiniBasketActions = (items: MiniBasketDisplayItem[], hookCart: CartView | null) => {
    // Track which line item groups are currently being updated to prevent race conditions
    const [updatingItems, setUpdatingItems] = useState(new Set<string>());

    const handleIncrement = useCallback(async (lineItemId: string) => {
        const cart = hookCart || await getCart();
        if (!cart) return;

        const lineItem = cart.lineItems.find((item: CTLineItem) => item.id === lineItemId);
        if (!lineItem) return;

        const action: CartAction = {
            action: 'changeLineItemQuantity',
            lineItemId: lineItemId,
            quantity: lineItem.quantity + 1
        };

        if (lineItem.priceMode === 'ExternalPrice') {
            action['externalPrice'] = lineItem.price.value;
        }

        await updateCart(action, cart);
    }, [hookCart]);

    const handleDecrement = useCallback(async (lineItemId: string) => {
        const cart = hookCart || await getCart();
        if (!cart) return;

        const lineItem = cart.lineItems.find((item: CTLineItem) => item.id === lineItemId);
        if (!lineItem) return;

        if (lineItem.quantity > 1) {
            const action: CartAction = {
                action: 'changeLineItemQuantity',
                lineItemId: lineItemId,
                quantity: lineItem.quantity - 1
            };

            if (lineItem.priceMode === 'ExternalPrice') {
                action['externalPrice'] = lineItem.price.value;
            }

            await updateCart(action, cart);
        } else {
            await updateCart({
                action: 'removeLineItem',
                lineItemId: lineItemId
            }, cart);
        }
    }, [hookCart]);

    const handleDelete = useCallback(async (lineItemId: string) => {
        await updateCart({
            action: 'removeLineItem',
            lineItemId: lineItemId
        });
    }, []);

    // Grouping logic
    const { groups, looseItems } = useMemo(() => {
        const g: Record<string, MiniBasketDisplayItem[]> = {};
        const l: MiniBasketDisplayItem[] = [];
        items.forEach(item => {
            const assocKey = item.custom?.fields?.['associationKey'] as string | undefined;
            const delegateRate = item.custom?.fields?.['delegateRate'];
            if (assocKey && delegateRate) {
                const groupKey = assocKey + '||' + delegateRate;
                if (!g[groupKey]) g[groupKey] = [];
                g[groupKey].push(item);
            } else if (assocKey) {
                if (!g[assocKey]) g[assocKey] = [];
                g[assocKey].push(item);
            } else {
                l.push(item);
            }
        });
        return { groups: g, looseItems: l };
    }, [items]);

    const handleGroupDelete = async (groupItems: MiniBasketDisplayItem[]) => {
        const mainItem = groupItems.find(i => isPassProduct(i)) || groupItems[0];
        const addOns = groupItems.filter(i => i !== mainItem);

        await removeItem({
            mainItem: normalizeToGroupedMainItem(mainItem),
            addOns: normalizeToAddOns(addOns)
        } as CartGroupView);
    };

    const handleGroupIncrement = async (groupItems: MiniBasketDisplayItem[]) => {
        const firstItem = groupItems[0];
        const assocKey = firstItem.custom?.fields?.['associationKey'] as string | undefined;
        const delegateRate = firstItem.custom?.fields?.['delegateRate'];
        const groupKey = assocKey && delegateRate
            ? `${assocKey}||${delegateRate}`
            : assocKey || groupItems.map(i => i.id).sort().join('_');

        if (updatingItems.has(groupKey)) return;

        setUpdatingItems(prev => new Set(prev).add(groupKey));

        try {
            const cart = hookCart || await getCart();
            if (!cart) return;

            const mainItems = groupItems.filter(i => isPassProduct(i));
            const currentTotalQty = mainItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
            const newQuantity = currentTotalQty + 1;

            const representativeMainItem = mainItems[0];
            const contextItem = {
                mainItem: normalizeToGroupedMainItem(representativeMainItem),
                addOns: normalizeToAddOns(groupItems.filter(i => !mainItems.includes(i)))
            };

            await updateQuantity(contextItem as CartGroupView, newQuantity, cart);
        } finally {
            setUpdatingItems(prev => {
                const next = new Set(prev);
                next.delete(groupKey);
                return next;
            });
        }
    };

    const handleGroupDecrement = async (groupItems: MiniBasketDisplayItem[]) => {
        const firstItem = groupItems[0];
        const assocKey = firstItem.custom?.fields?.['associationKey'] as string | undefined;
        const delegateRate = firstItem.custom?.fields?.['delegateRate'];
        const groupKey = assocKey && delegateRate
            ? `${assocKey}||${delegateRate}`
            : assocKey || groupItems.map(i => i.id).sort().join('_');

        if (updatingItems.has(groupKey)) return;

        setUpdatingItems(prev => new Set(prev).add(groupKey));

        try {
            const mainItems = groupItems.filter(i => isPassProduct(i));
            const currentTotalQty = mainItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

            if (currentTotalQty > 1) {
                const cart = hookCart || await getCart();
                if (!cart) return;

                const representativeMainItem = mainItems[0];
                const contextItem = {
                    mainItem: normalizeToGroupedMainItem(representativeMainItem),
                    addOns: normalizeToAddOns(groupItems.filter(i => !mainItems.includes(i)))
                };

                const newQuantity = currentTotalQty - 1;
                await updateQuantity(contextItem as CartGroupView, newQuantity, cart);
            } else {
                await removeItem({
                    mainItem: normalizeToGroupedMainItem(mainItems[0]),
                    addOns: normalizeToAddOns(groupItems.filter(i => !mainItems.includes(i)))
                } as CartGroupView);
            }
        } finally {
            setUpdatingItems(prev => {
                const next = new Set(prev);
                next.delete(groupKey);
                return next;
            });
        }
    };

    const subtotal = useMemo(() => {
        return items.reduce((sum, item) => {
            const itemTotal = item.totalPrice?.centAmount ? (item.totalPrice.centAmount / 100) : (item.price * item.quantity);
            return sum + itemTotal;
        }, 0);
    }, [items]);

    const groupedCount = useMemo(() => {
        let count = 0;
        Object.values(groups).forEach(group => {
            const mainItems = group.filter(item => isPassProduct(item));
            const itemsToCount = mainItems.length > 0 ? mainItems : [group[0]];
            itemsToCount.forEach(item => {
                count += item.quantity || 0;
            });
        });
        looseItems.forEach(item => {
            count += item.quantity;
        });
        return count;
    }, [groups, looseItems]);

    return {
        groups,
        looseItems,
        updatingItems,
        subtotal,
        groupedCount,
        handleIncrement,
        handleDecrement,
        handleDelete,
        handleGroupDelete,
        handleGroupIncrement,
        handleGroupDecrement
    };
};
